// src/pages/masters/PriceHistoryPage.js
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Card, CardHeader, Btn, Input, Select, Table, formatCurrency, PageHeader } from '../../components/ui';
import { Plus, History } from 'lucide-react';

const initialForm = {
  script_id: '', trade_date: new Date().toISOString().split('T')[0],
  open_price: '', high_price: '', low_price: '', close_price: '', volume: ''
};

export default function PriceHistoryPage() {
  const [scripts, setScripts] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({ script_id: '', from_date: '', to_date: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/masters/scripts').then(r => setScripts(r.data)); }, []);

  const loadHistory = () => {
    if (!filter.script_id) return;
    api.get('/masters/price-history', { params: filter }).then(r => setHistory(r.data));
  };

  useEffect(() => { if (filter.script_id) loadHistory(); }, [filter.script_id]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/masters/price-history', form);
      toast.success('Price data saved!');
      setForm(initialForm); setShowForm(false);
      if (filter.script_id) loadHistory();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setF = (k) => (e) => setFilter(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        title="Price History"
        subtitle="Manage historical OHLCV data for scripts"
        action={<Btn onClick={() => setShowForm(!showForm)}><Plus size={16} /> Add Price Data</Btn>}
      />

      {showForm && (
        <Card style={{ marginBottom: 24 }}>
          <CardHeader title="Add / Update Price Data" subtitle="OHLCV data (upsert on script + date)" />
          <form onSubmit={handleSubmit} style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
              <Select label="Script *" value={form.script_id} onChange={set('script_id')} style={{ gridColumn: '1 / 3' }}>
                <option value="">Select Script</option>
                {scripts.map(s => <option key={s.id} value={s.id}>[{s.exchange_code}] {s.script_code} - {s.script_name}</option>)}
              </Select>
              <Input label="Trade Date *" type="date" value={form.trade_date} onChange={set('trade_date')} />
              <Input label="Open Price" type="number" step="0.01" value={form.open_price} onChange={set('open_price')} placeholder="0.00" />
              <Input label="High Price" type="number" step="0.01" value={form.high_price} onChange={set('high_price')} placeholder="0.00" />
              <Input label="Low Price" type="number" step="0.01" value={form.low_price} onChange={set('low_price')} placeholder="0.00" />
              <Input label="Close Price *" type="number" step="0.01" value={form.close_price} onChange={set('close_price')} placeholder="0.00" />
              <Input label="Volume" type="number" value={form.volume} onChange={set('volume')} placeholder="Shares traded" />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save Price Data'}</Btn>
              <Btn variant="ghost" onClick={() => setShowForm(false)}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Filter */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ padding: '16px 24px', display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Select label="Script" value={filter.script_id} onChange={setF('script_id')} style={{ minWidth: 260 }}>
            <option value="">Select Script to view history</option>
            {scripts.map(s => <option key={s.id} value={s.id}>[{s.exchange_code}] {s.script_code} - {s.script_name}</option>)}
          </Select>
          <Input label="From Date" type="date" value={filter.from_date} onChange={setF('from_date')} style={{ minWidth: 160 }} />
          <Input label="To Date" type="date" value={filter.to_date} onChange={setF('to_date')} style={{ minWidth: 160 }} />
          <Btn onClick={loadHistory} disabled={!filter.script_id} style={{ marginTop: 20 }}><History size={14} /> Load</Btn>
        </div>
      </Card>

      <Card>
        <CardHeader title="Price History" subtitle={`${history.length} records`} />
        <Table
          headers={['Date', 'Script', 'Exchange', 'Open', 'High', 'Low', 'Close', 'Volume']}
          rows={history.map(h => [
            h.trade_date?.split('T')[0],
            <strong>{h.script_code}</strong>,
            h.script_name,
            formatCurrency(h.open_price),
            formatCurrency(h.high_price),
            formatCurrency(h.low_price),
            <strong>{formatCurrency(h.close_price)}</strong>,
            h.volume ? parseInt(h.volume).toLocaleString('en-IN') : '—'
          ])}
          emptyMessage="Select a script above to view its price history."
        />
      </Card>
    </div>
  );
}
